import { Box, ListSubheader, MenuList } from '@mui/material';
import { Fragment, useCallback, useMemo, useState, type FC } from 'react';
import {
    groupedReferencesByRubricIdQuery,
    selectedReferenceByRubricIdQuery,
    sortedReferencesByRubricIdQuery,
} from '../../../../../core/catalogs/queries';
import type { Reference } from '../../../../../core/domain/Roaddata';
import { translateReference } from '../../../../../core/legacy/utils/translations';
import { SelectReferenceByRubricIdCommand } from '../../../../../core/catalogs/commands/SelectReferenceByRubricIdCommand';
import { useAppDispatch, useAppSelector } from '../../../../../types';
import { StyledMenu, StyledMenuItem } from '../../ui/Menu/style';
import { selectedRubricSelector } from '../../../../../core/legacy/selectors/streetView';

export type Position = {
    left: number;
    top: number;
};

type Props = {
    open: boolean;
    position: Position;
    handleCloseContextMenu: () => void;
};

export const ToolContextMenu: FC<Props> = ({ open, position, handleCloseContextMenu }) => {
    const dispatch = useAppDispatch();
    const selectedRubric = useAppSelector(selectedRubricSelector);
    const sortedReferencesSelector = useMemo(
        () => sortedReferencesByRubricIdQuery(selectedRubric?.id),
        [selectedRubric?.id],
    );
    const groupedReferencesSelector = useMemo(
        () => groupedReferencesByRubricIdQuery(selectedRubric?.id),
        [selectedRubric?.id],
    );
    const references = useAppSelector(sortedReferencesSelector);
    const grouped = useAppSelector(groupedReferencesSelector);
    const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());

    const toggleParent = useCallback((parentId: string) => {
        setExpandedParents((prev) => {
            const next = new Set(prev);
            if (next.has(parentId)) next.delete(parentId);
            else next.add(parentId);
            return next;
        });
    }, []);

    const handleClickItem = (reference: Reference) => () => {
        if (selectedRubric) {
            dispatch(SelectReferenceByRubricIdCommand({ rubricId: selectedRubric.id, reference }));
        }
        handleCloseContextMenu();
    };

    const selectedRubriceference = useAppSelector(selectedReferenceByRubricIdQuery(selectedRubric?.id));

    const isSV = selectedRubric?.code === 'SV';

    const renderReferences = () => {
        if (grouped.length > 0) {
            return grouped.map((group) => {
                // Standalone ref: no children — render as a plain selectable item without subheader
                if (group.children.length === 0) {
                    return (
                        <StyledMenuItem
                            key={group.parent.id}
                            selected={group.parent.id === selectedRubriceference?.id}
                            onClick={handleClickItem(group.parent)}
                        >
                            {translateReference(group.parent)}
                        </StyledMenuItem>
                    );
                }

                const isExpanded = expandedParents.has(group.parent.id);

                return (
                    <Fragment key={group.parent.id}>
                        <ListSubheader
                            disableSticky
                            onClick={() => toggleParent(group.parent.id)}
                            sx={{
                                fontWeight: 'bold',
                                lineHeight: '30px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                            }}
                        >
                            <Box component="span" sx={{ display: 'inline-block', width: '12px' }}>
                                {isExpanded ? '−' : '+'}
                            </Box>
                            {group.parent.name}
                        </ListSubheader>
                        {/* SV parents are valid annotations themselves; other rubric parents are grouping labels only.
                            Only shown once expanded, alongside the children, not as an always-visible row. */}
                        {isExpanded && isSV && (
                            <StyledMenuItem
                                selected={group.parent.id === selectedRubriceference?.id}
                                onClick={handleClickItem(group.parent)}
                                sx={{ pl: 3 }}
                            >
                                {translateReference(group.parent)}
                            </StyledMenuItem>
                        )}
                        {isExpanded &&
                            group.children.map((child) => (
                                <StyledMenuItem
                                    key={child.id}
                                    selected={child.id === selectedRubriceference?.id}
                                    onClick={handleClickItem(child)}
                                    sx={{ pl: isSV ? 5 : 3 }}
                                >
                                    {translateReference(child)}
                                </StyledMenuItem>
                            ))}
                    </Fragment>
                );
            });
        }

        return references.map((reference) => (
            <StyledMenuItem
                key={reference.id}
                selected={reference.id === selectedRubriceference?.id}
                onClick={handleClickItem(reference)}
            >
                {translateReference(reference)}
            </StyledMenuItem>
        ));
    };

    return (
        <StyledMenu
            open={open}
            anchorPosition={position}
            anchorReference="anchorPosition"
            onClose={handleCloseContextMenu}
        >
            <MenuList
                onWheel={(e) => e.stopPropagation()}
                sx={{ maxHeight: 400, overflow: 'auto' }}
            >
                {renderReferences()}
            </MenuList>
        </StyledMenu>
    );
};
